import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconXClose from "../../../img/icons/x-close.svg";
import { signupForEmailOffers } from "../../api/user";
import { config } from "../../config";
import { CaptchaLibrary } from "../../forms/Captcha";
import {
    FLOATING_LABEL_CLASS_NAME,
    FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
    PSEUDO_PLACEHOLDER_CLASS_NAME,
} from "../../forms/classNames";
import { IStickyEmailBanner } from "../../models/sticky-email-banner";
import { RichText } from "../RichText";

const TRUE = "true";
const LOCAL_STORAGE_KEY_CLOSED = "email-signup-modal-closed";

const getHasBeenSubmittedOrClosed = (): boolean => {
    const window = getWindow();
    if (!window?.localStorage) {
        return false;
    }
    try {
        return window.localStorage[LOCAL_STORAGE_KEY_CLOSED] === TRUE;
    } catch (e) {
        console.error(e);
    }
    return false;
};

const setHasBeenSubmittedOrClosed = (): void => {
    const window = getWindow();
    if (!window?.localStorage) {
        return;
    }
    try {
        window.localStorage[LOCAL_STORAGE_KEY_CLOSED] = TRUE;
    } catch (e) {
        console.error(e);
    }
};

export const StickyEmailBanner = (
    props: Immutable<{
        bannerData: IStickyEmailBanner;
    }>,
): React.ReactNode => {
    const [showBanner, setShowBanner] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [hasBeenSubmittedOrClosed] = React.useState(() =>
        getHasBeenSubmittedOrClosed(),
    );
    const [email, setEmail] = React.useState("");
    const [errors, setErrors] = React.useState<string[]>([]);

    const emailInputElem = React.useRef<HTMLInputElement>(null);
    const origFocusedElem = React.useRef<Element | null>(null);

    // Mount: Setup scroll listener
    React.useEffect(() => {
        const window = getWindow();
        const document = getDocument();
        if (!window || !document) return;

        const scrollDistanceUntilAppearance = 300;
        const startingScrollY = window.scrollY;

        const handleScroll = () => {
            if (!window) return;
            if (!getHasBeenSubmittedOrClosed()) {
                const currentScrollY = window.scrollY;
                if (
                    Math.abs(startingScrollY - currentScrollY) >
                    scrollDistanceUntilAppearance
                ) {
                    setShowBanner(true);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            if (window) {
                window.removeEventListener("scroll", handleScroll);
            }
            if (document) {
                document.body.classList.remove("sticky-email-banner--closed");
                document.body.classList.remove("sticky-email-banner--open");
            }
        };
    }, []);

    // Update: Manage body classes based on showBanner state
    React.useEffect(() => {
        const document = getDocument();
        if (!document) return;

        if (showBanner) {
            document.body.classList.remove("sticky-email-banner--closed");
            document.body.classList.add("sticky-email-banner--open");
        } else {
            document.body.classList.add("sticky-email-banner--closed");
            document.body.classList.remove("sticky-email-banner--open");
        }
    }, [showBanner]);

    const closeModal = () => {
        setShowBanner(false);
        // restore focus to originally focused element
        if (origFocusedElem.current) {
            (origFocusedElem.current as HTMLElement).focus();
        }
    };

    const onEmailChange = (e: React.FormEvent<HTMLInputElement>) => {
        setEmail(e.currentTarget.value);
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        try {
            await signupForEmailOffers(email);
            setShowBanner(false);
            setSubmitted(true);
            setHasBeenSubmittedOrClosed();
        } catch (err) {
            setErrors(
                err.response.body.email || [t`An unexpected error occured.`],
            );
        }
    };

    if (hasBeenSubmittedOrClosed) {
        return null;
    }

    return (
        <section className="l-full-width email-subscription">
            {config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA") && <CaptchaLibrary />}
            <div className="l-capped-width">
                <RichText html={props.bannerData.content} />
                <div className="email-sign-up">
                    <form onSubmit={onSubmit}>
                        <div
                            className={concatClassNames([
                                FLOATING_LABEL_CLASS_NAME,
                                FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
                            ])}
                        >
                            <input
                                placeholder=" "
                                type="email"
                                required={true}
                                value={email}
                                onChange={onEmailChange}
                                id="sticky-email-input"
                                ref={emailInputElem}
                            />
                            <span
                                className={PSEUDO_PLACEHOLDER_CLASS_NAME}
                                aria-hidden={true}
                            >
                                {t`Email Address`}
                            </span>
                            <label htmlFor="sticky-email-input">
                                {t`Email Address`}
                            </label>
                            <input
                                type="submit"
                                value={
                                    !submitted
                                        ? props.bannerData.submitButtonText
                                        : props.bannerData.submitedButtonText
                                }
                            />
                        </div>
                    </form>
                    {errors.map((msg, i) => {
                        return (
                            <p key={i} className="email-sign-up__error">
                                {msg}
                            </p>
                        );
                    })}
                </div>
            </div>
            <button
                onClick={() => {
                    closeModal();
                }}
            >
                <span className="ada-screenreader-only">Close</span>
                <SVG role="img" src={iconXClose} title={t`Close Icon`} />
            </button>
        </section>
    );
};
